import { init, registerPlugins, registerRemotes } from '@module-federation/runtime';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as styled from 'styled-components';
import * as mui from '@material-ui/core';
import reactPkg from 'react/package.json';
import reactDomPkg from 'react-dom/package.json';
import styledPkg from 'styled-components/package.json';
import muiPkg from '@material-ui/core/package.json';

import type { RemoteExtensionsConfiguration } from 'src/gql/graphql';
import PluginOfflineRemotes from './plugin-offline-remotes';

type Remote = {
  name: string;
  alias: string;
  entry: string;
};

type SharedDependency = {
  version: string;
  lib: () => unknown;
  shareConfig: {
    singleton: boolean;
    requiredVersion: string;
  };
};

type SharedDependencies = Record<string, SharedDependency>;

type ModuleFederationConfig = {
  name: string;
  remotes: Remote[];
  shareStrategy: 'loaded-first';
  shared: SharedDependencies;
};

const sharedDependenciesConfigFactory = (
  pkgs: Array<[{ name: string; version: string }, unknown]>
): SharedDependencies => {
  const configObj: SharedDependencies = {};
  pkgs.forEach(([pkg, lib]) => {
    configObj[pkg.name] = {
      version: pkg.version,
      lib: () => lib,
      shareConfig: {
        singleton: true,
        requiredVersion: `^${String(pkg.version.split('.')[0])}`,
      },
    };
  });
  return configObj;
};

/**
 * Initializes Module Federation with the configured remotes and shared dependencies
 */
export const initializeModuleFederation = (remoteExtensionsConfig?: RemoteExtensionsConfiguration): void => {
  // This is not an async function, it's just a one-time initialization
  // That sets the RUNTIME remotes for the host app
  // The shared packages are loaded at build time in next.config.js
  const config: ModuleFederationConfig = {
    name: '@dutchie/host',
    remotes: [],
    shareStrategy: 'loaded-first',
    shared: sharedDependenciesConfigFactory([
      [reactPkg, React],
      [reactDomPkg, ReactDOM],
      [styledPkg, styled],
      [muiPkg, mui],
    ]),
  };

  init(config);
  registerPlugins([PluginOfflineRemotes()]);

  // If remote extensions config is provided, ensure the remote is initialized
  if (remoteExtensionsConfig) {
    ensureRemoteInitialized({
      manifestUrl: remoteExtensionsConfig.manifestUrl ?? '',
      name: remoteExtensionsConfig.scope ?? '',
    });
  }

  try {
    if (typeof window !== 'undefined') {
      (window as Window & { __MF_INITIALIZED?: boolean }).__MF_INITIALIZED = true;
    }
  } catch (e) {
    console.error('Error setting __MF_INITIALIZED', e);
  }
};

// registerRemotes() silently keeps the first-registered manifestUrl for a given `name` unless
// `force: true` is passed. CMS-preview dispensaries all share the same `name` (the theme's
// `scope`), so without tracking history here, switching "Previewing as" would keep loading
// whichever dispensary's theme registered first.
const registeredManifestUrlsByName = new Map<string, string>();

// Test-only reset for the map above; not called from application code.
export const resetRegisteredManifestUrlsForTesting = (): void => {
  registeredManifestUrlsByName.clear();
};

export const ensureRemoteInitialized = ({ manifestUrl, name }: { manifestUrl: string; name: string }): boolean => {
  if (!manifestUrl || !name) {
    console.error('manifestUrl and name are required to register a remote', { manifestUrl, name });
    return false;
  }

  const previousManifestUrl = registeredManifestUrlsByName.get(name);
  const force = previousManifestUrl !== undefined && previousManifestUrl !== manifestUrl;
  const remote: Remote = { name, alias: name, entry: manifestUrl };

  // Only force when the URL actually changed, so routine re-renders of the same store's theme
  // don't trigger MF's "may cause unexpected errors" force-path warning.
  if (force) {
    registerRemotes([remote], { force: true });
  } else {
    registerRemotes([remote]);
  }
  registeredManifestUrlsByName.set(name, manifestUrl);
  return true;
};
